import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Plus, Calendar as CalendarIcon, LayoutGrid, ChevronLeft, ChevronRight,
  Clock, CheckCircle2, AlertCircle, Eye, Trash2, Edit, X, MessageSquare,
  Send, Tag, Flag, GripVertical, Megaphone, Mail, Globe, PartyPopper,
  FileText, Users, Camera, MoreVertical, Link2, Search, Building2, ShoppingCart,
  Paperclip, Upload, Download, Image, File, Activity, ArrowRight
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUSES = [
  { id: 'todo', label: 'A faire', color: '#FBBF24', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400' },
  { id: 'in_progress', label: 'En cours', color: '#3B82F6', bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400' },
  { id: 'to_validate', label: 'A valider', color: '#F97316', bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400' },
  { id: 'done', label: 'Termine', color: '#22C55E', bg: 'bg-green-500/10', border: 'border-green-500/30', text: 'text-green-400' },
];

const CATEGORIES = [
  { id: 'reseaux_sociaux', label: 'Reseaux sociaux', icon: Megaphone },
  { id: 'emailing', label: 'Emailing', icon: Mail },
  { id: 'site_web_seo', label: 'Site web / SEO', icon: Globe },
  { id: 'evenements', label: 'Evenements', icon: PartyPopper },
  { id: 'print', label: 'Print / Flyers', icon: FileText },
  { id: 'partenariats', label: 'Partenariats', icon: Users },
  { id: 'photo_video', label: 'Photo / Video', icon: Camera },
  { id: 'autre', label: 'Autre', icon: Tag },
];

const PRIORITIES = [
  { id: 'low', label: 'Basse', color: '#6B7280' },
  { id: 'medium', label: 'Moyenne', color: '#3B82F6' },
  { id: 'high', label: 'Haute', color: '#F97316' },
  { id: 'urgent', label: 'Urgente', color: '#EF4444' },
];

const getStatus = (id) => STATUSES.find(s => s.id === id) || STATUSES[0];
const getCategory = (id) => CATEGORIES.find(c => c.id === id) || CATEGORIES[7];
const getPriority = (id) => PRIORITIES.find(p => p.id === id) || PRIORITIES[1];

const DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS_FR = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];

export default function Marketing() {
  const { token } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [view, setView] = useState('calendar');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draggedTask, setDraggedTask] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [calMode, setCalMode] = useState('month');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dayDetailDate, setDayDetailDate] = useState(null);
  const [prospects, setProspects] = useState([]);

  const api = useCallback(async (method, path, body) => {
    const opts = { method: method.toUpperCase(), headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } };
    if (body) opts.body = JSON.stringify(body);
    const r = await fetch(`${API_URL}/api/marketing${path}`, opts);
    if (!r.ok) throw new Error(await r.text());
    return r.json();
  }, [token]);

  const loadData = useCallback(async () => {
    try {
      const [t, s, p] = await Promise.all([api('GET', '/tasks'), api('GET', '/stats'), api('GET', '/prospects-list')]);
      setTasks(t);
      setStats(s);
      setProspects(p);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSaveTask = async (data) => {
    try {
      if (editingTask?.id) {
        await api('PUT', `/tasks/${editingTask.id}`, data);
        toast.success('Tache mise a jour');
      } else {
        await api('POST', '/tasks', data);
        toast.success('Tache creee');
      }
      setShowModal(false);
      setEditingTask(null);
      loadData();
    } catch { toast.error('Erreur'); }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await api('PUT', `/tasks/${taskId}`, { status: newStatus });
      loadData();
      if (selectedTask?.id === taskId) {
        const updated = await api('GET', `/tasks/${taskId}`);
        setSelectedTask(updated);
      }
    } catch { toast.error('Erreur'); }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Supprimer cette tache ?')) return;
    try {
      await api('DELETE', `/tasks/${taskId}`);
      toast.success('Tache supprimee');
      setSelectedTask(null);
      loadData();
    } catch { toast.error('Erreur'); }
  };

  const handleComment = async (taskId, text) => {
    try {
      await api('POST', `/tasks/${taskId}/comments`, { text });
      const updated = await api('GET', `/tasks/${taskId}`);
      setSelectedTask(updated);
      loadData();
    } catch { toast.error('Erreur'); }
  };

  const handleReloadTask = async () => {
    if (!selectedTask?.id) return;
    try {
      const updated = await api('GET', `/tasks/${selectedTask.id}`);
      setSelectedTask(updated);
      loadData();
    } catch { console.error('Reload failed'); }
  };

  const handleSelectTask = async (task) => {
    try {
      const full = await api('GET', `/tasks/${task.id}`);
      setSelectedTask(full);
    } catch { setSelectedTask(task); }
  };

  if (loading) return <div className="min-h-screen bg-[#141617] flex items-center justify-center"><div className="text-[#8C8F94]">Chargement...</div></div>;

  return (
    <div className="min-h-screen bg-[#141617] p-6" data-testid="marketing-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Marketing</h1>
          <p className="text-sm text-[#8C8F94]">Suivi communication & taches</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-[#1D2327] rounded-lg p-1 border border-[#2C3338]">
            <button onClick={() => setView('calendar')} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-all ${view === 'calendar' ? 'bg-[#00E01A] text-black font-medium' : 'text-[#8C8F94] hover:text-white'}`} data-testid="view-calendar-btn">
              <CalendarIcon size={16} />Calendrier
            </button>
            <button onClick={() => setView('kanban')} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-all ${view === 'kanban' ? 'bg-[#00E01A] text-black font-medium' : 'text-[#8C8F94] hover:text-white'}`} data-testid="view-kanban-btn">
              <LayoutGrid size={16} />Kanban
            </button>
          </div>
          <button onClick={() => { setEditingTask(null); setShowModal(true); }} className="flex items-center gap-2 px-4 py-2 bg-[#00E01A] text-black font-medium rounded-lg text-sm hover:bg-[#00C016] transition-colors" data-testid="new-task-btn">
            <Plus size={16} />Nouvelle tache
          </button>
        </div>
      </div>

      {/* KPIs */}
      <StatsBar stats={stats} activeFilter={statusFilter} onFilterClick={(f) => { setStatusFilter(f === statusFilter ? null : f); setView('kanban'); }} />

      {/* Views */}
      {view === 'calendar' ? (
        <CalendarView tasks={tasks} currentMonth={currentMonth} setCurrentMonth={setCurrentMonth} selectedDate={selectedDate} setSelectedDate={setSelectedDate} calMode={calMode} setCalMode={setCalMode} onTaskClick={handleSelectTask} onStatusChange={handleStatusChange} onEdit={(t) => { setEditingTask(t); setShowModal(true); }} onDayClick={(date) => setDayDetailDate(date)} />
      ) : (
        <KanbanView tasks={statusFilter ? tasks.filter(t => t.status === statusFilter) : tasks} onTaskClick={handleSelectTask} onStatusChange={handleStatusChange} onEdit={(t) => { setEditingTask(t); setShowModal(true); }} draggedTask={draggedTask} setDraggedTask={setDraggedTask} statusFilter={statusFilter} />
      )}

      {/* Day Detail Modal */}
      {dayDetailDate && <DayDetailModal dateStr={dayDetailDate} tasks={tasks} onClose={() => setDayDetailDate(null)} onTaskClick={handleSelectTask} onEdit={(t) => { setDayDetailDate(null); setEditingTask(t); setShowModal(true); }} onNewTask={() => { setDayDetailDate(null); setEditingTask({ start_date: dayDetailDate, deadline: dayDetailDate }); setShowModal(true); }} onStatusChange={handleStatusChange} />}

      {/* Create/Edit Modal */}
      {showModal && <TaskFormModal task={editingTask} onSave={handleSaveTask} onClose={() => { setShowModal(false); setEditingTask(null); }} prospects={prospects} />}

      {/* Detail Modal */}
      {selectedTask && <TaskDetailModal task={selectedTask} onClose={() => setSelectedTask(null)} onEdit={(t) => { setSelectedTask(null); setEditingTask(t); setShowModal(true); }} onDelete={handleDelete} onComment={handleComment} onStatusChange={handleStatusChange} onReload={handleReloadTask} />}
    </div>
  );
}

// ==================== STATS BAR ====================
function StatsBar({ stats, activeFilter, onFilterClick }) {
  if (!stats) return null;
  const items = [
    { label: 'Total', value: stats.total, color: 'text-white', filter: null },
    { label: 'A faire', value: stats.by_status?.todo || 0, color: 'text-yellow-400', filter: 'todo' },
    { label: 'En cours', value: stats.by_status?.in_progress || 0, color: 'text-blue-400', filter: 'in_progress' },
    { label: 'A valider', value: stats.by_status?.to_validate || 0, color: 'text-orange-400', filter: 'to_validate' },
    { label: 'Termine', value: stats.by_status?.done || 0, color: 'text-green-400', filter: 'done' },
    { label: 'Completion', value: `${stats.completion_rate}%`, color: 'text-[#00E01A]', filter: null },
  ];
  return (
    <div className="grid grid-cols-6 gap-3 mb-6" data-testid="marketing-stats">
      {items.map(i => (
        <button key={i.label} onClick={() => i.filter !== undefined && onFilterClick(i.filter)} className={`bg-[#1D2327] rounded-xl border p-4 text-left transition-all ${activeFilter === i.filter && i.filter ? 'border-[#00E01A] ring-1 ring-[#00E01A]/20' : 'border-[#2C3338] hover:border-[#555]'} ${i.filter !== null ? 'cursor-pointer' : ''}`} data-testid={`stat-${i.filter || i.label}`}>
          <p className="text-[#8C8F94] text-xs mb-1">{i.label}</p>
          <p className={`text-2xl font-bold ${i.color}`}>{i.value}</p>
        </button>
      ))}
    </div>
  );
}

// ==================== CALENDAR VIEW (MULTI-MODE) ====================
const CAL_MODES = [
  { id: 'day', label: 'Jour' },
  { id: '3days', label: '3 jours' },
  { id: 'week', label: 'Semaine' },
  { id: '15days', label: '15 jours' },
  { id: 'month', label: 'Mois' },
];
const HOURS = Array.from({ length: 14 }, (_, i) => i + 7); // 7h-20h

function CalendarView({ tasks, currentMonth, setCurrentMonth, selectedDate, setSelectedDate, calMode, setCalMode, onTaskClick, onStatusChange, onEdit, onDayClick }) {
  const today = new Date();
  const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const todayStr = fmtDate(today);

  // Get array of dates to display based on mode
  const dates = useMemo(() => {
    const base = selectedDate || new Date();
    const arr = [];
    if (calMode === 'day') {
      arr.push(new Date(base));
    } else if (calMode === '3days') {
      for (let i = 0; i < 3; i++) { const d = new Date(base); d.setDate(d.getDate() + i); arr.push(d); }
    } else if (calMode === 'week') {
      const start = new Date(base);
      const dow = (start.getDay() + 6) % 7;
      start.setDate(start.getDate() - dow);
      for (let i = 0; i < 7; i++) { const d = new Date(start); d.setDate(d.getDate() + i); arr.push(d); }
    } else if (calMode === '15days') {
      for (let i = 0; i < 15; i++) { const d = new Date(base); d.setDate(d.getDate() + i); arr.push(d); }
    }
    return arr;
  }, [selectedDate, calMode]);

  const tasksForDate = useCallback((dateStr) => {
    return tasks.filter(t => {
      const d = t.deadline || t.start_date;
      if (!d) return false;
      // Parse to local date to avoid timezone issues
      const td = new Date(d);
      const tdStr = `${td.getFullYear()}-${String(td.getMonth() + 1).padStart(2, '0')}-${String(td.getDate()).padStart(2, '0')}`;
      return tdStr === dateStr;
    });
  }, [tasks]);

  const navigate = (dir) => {
    const d = new Date(selectedDate);
    const steps = { day: 1, '3days': 3, week: 7, '15days': 15, month: 0 };
    if (calMode === 'month') {
      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + dir, 1));
    } else {
      d.setDate(d.getDate() + dir * (steps[calMode] || 1));
      setSelectedDate(d);
    }
  };

  const goToday = () => {
    setSelectedDate(new Date());
    setCurrentMonth(new Date());
  };

  const headerLabel = () => {
    if (calMode === 'month') return `${MONTHS_FR[currentMonth.getMonth()]} ${currentMonth.getFullYear()}`;
    if (calMode === 'day') return selectedDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    if (dates.length > 0) {
      const first = dates[0];
      const last = dates[dates.length - 1];
      if (first.getMonth() === last.getMonth()) return `${first.getDate()} - ${last.getDate()} ${MONTHS_FR[first.getMonth()]} ${first.getFullYear()}`;
      return `${first.getDate()} ${MONTHS_FR[first.getMonth()].slice(0, 3)} - ${last.getDate()} ${MONTHS_FR[last.getMonth()].slice(0, 3)} ${last.getFullYear()}`;
    }
    return '';
  };

  return (
    <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] overflow-hidden" data-testid="calendar-view">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-[#2C3338]">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="p-1.5 text-[#8C8F94] hover:text-white hover:bg-[#2C3338] rounded-lg transition-colors"><ChevronLeft size={18} /></button>
          <button onClick={() => navigate(1)} className="p-1.5 text-[#8C8F94] hover:text-white hover:bg-[#2C3338] rounded-lg transition-colors"><ChevronRight size={18} /></button>
          <button onClick={goToday} className="px-3 py-1 text-xs text-[#8C8F94] hover:text-white border border-[#2C3338] rounded-lg hover:bg-[#2C3338] transition-colors" data-testid="cal-today-btn">Aujourd'hui</button>
          <h2 className="text-sm font-bold text-white ml-2 capitalize">{headerLabel()}</h2>
        </div>
        <div className="flex bg-[#141617] rounded-lg p-0.5 border border-[#2C3338]">
          {CAL_MODES.map(m => (
            <button key={m.id} onClick={() => setCalMode(m.id)} className={`px-3 py-1 rounded-md text-xs transition-all ${calMode === m.id ? 'bg-[#00E01A] text-black font-medium' : 'text-[#8C8F94] hover:text-white'}`} data-testid={`cal-mode-${m.id}`}>
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* Month view */}
      {calMode === 'month' && <MonthGrid currentMonth={currentMonth} tasks={tasks} tasksForDate={tasksForDate} todayStr={todayStr} onTaskClick={onTaskClick} onDayClick={onDayClick} setSelectedDate={setSelectedDate} setCalMode={setCalMode} />}

      {/* Day / 3days / Week / 15days views */}
      {calMode !== 'month' && (
        calMode === '15days' ? (
          <FortNightGrid dates={dates} tasksForDate={tasksForDate} todayStr={todayStr} onTaskClick={onTaskClick} onDayClick={onDayClick} />
        ) : (
          <TimeGrid dates={dates} tasksForDate={tasksForDate} todayStr={todayStr} onTaskClick={onTaskClick} onDayClick={onDayClick} calMode={calMode} />
        )
      )}
    </div>
  );
}

// ==================== MONTH GRID (existing) ====================
function MonthGrid({ currentMonth, tasks, tasksForDate, todayStr, onTaskClick, onDayClick, setSelectedDate, setCalMode }) {
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDow = (firstDay.getDay() + 6) % 7;
  const daysInMonth = lastDay.getDate();

  const tasksByDay = useMemo(() => {
    const map = {};
    tasks.forEach(t => {
      const d = t.deadline || t.start_date;
      if (!d) return;
      const date = new Date(d);
      if (date.getMonth() === month && date.getFullYear() === year) {
        const key = date.getDate();
        if (!map[key]) map[key] = [];
        map[key].push(t);
      }
    });
    return map;
  }, [tasks, month, year]);

  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const fmtDate = (y, m, d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const isToday = (d) => fmtDate(year, month, d) === todayStr;

  return (
    <>
      <div className="grid grid-cols-7 border-b border-[#2C3338]">
        {DAYS_FR.map(d => <div key={d} className="p-2 text-center text-xs font-medium text-[#8C8F94]">{d}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((day, i) => {
          const dateStr = day ? fmtDate(year, month, day) : null;
          return (
            <div key={i} className={`min-h-[100px] border-r border-b border-[#2C3338] p-1.5 ${day ? 'bg-[#1D2327] cursor-pointer hover:bg-[#232829]' : 'bg-[#191C1E]'} ${isToday(day) ? 'ring-1 ring-inset ring-[#00E01A]/40' : ''} transition-colors`} onClick={() => day && onDayClick(dateStr)}>
              {day && (
                <>
                  <div className="flex items-center justify-between">
                    <button onClick={(e) => { e.stopPropagation(); setSelectedDate(new Date(year, month, day)); setCalMode('day'); }} className={`text-xs font-medium hover:underline ${isToday(day) ? 'bg-[#00E01A] text-black w-6 h-6 rounded-full flex items-center justify-center' : 'text-[#8C8F94]'}`}>{day}</button>
                    <Plus size={12} className="text-[#2C3338] hover:text-[#00E01A] transition-colors" />
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {(tasksByDay[day] || []).slice(0, 3).map(t => {
                      const st = getStatus(t.status);
                      return (
                        <button key={t.id} onClick={(e) => { e.stopPropagation(); onTaskClick(t); }} className={`w-full text-left px-1.5 py-0.5 rounded text-[10px] truncate border ${st.bg} ${st.border} ${st.text} hover:opacity-80 transition-opacity flex items-center gap-0.5`} data-testid={`cal-task-${t.id}`}>
                          {t.linked_prospect_name && <Link2 size={8} className="text-purple-400 flex-shrink-0" />}
                          <span className="truncate">{t.title}</span>
                        </button>
                      );
                    })}
                    {(tasksByDay[day] || []).length > 3 && <span className="text-[10px] text-[#8C8F94] px-1">+{tasksByDay[day].length - 3}</span>}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

// ==================== TIME GRID (day / 3days / week) ====================
function TimeGrid({ dates, tasksForDate, todayStr, onTaskClick, onDayClick, calMode }) {
  const isDay = calMode === 'day';

  // Helper to detect if a task has a specific time (not midnight)
  const hasSpecificTime = (t) => {
    const d = t.deadline || t.start_date;
    if (!d) return false;
    // Check if the ISO string has a non-midnight time
    return !d.includes('T00:00:00');
  };

  const getTaskHour = (t) => {
    const d = t.deadline || t.start_date;
    if (!d) return null;
    const date = new Date(d);
    return date.getUTCHours(); // Use UTC to be consistent
  };

  return (
    <div className="flex overflow-x-auto">
      {/* Time labels */}
      <div className="w-14 flex-shrink-0 border-r border-[#2C3338]">
        <div className="h-10 border-b border-[#2C3338] flex items-center justify-end pr-2"><span className="text-[10px] text-[#555]">Journee</span></div>
        {HOURS.map(h => (
          <div key={h} className="h-14 border-b border-[#2C3338] flex items-start justify-end pr-2 pt-1">
            <span className="text-[10px] text-[#555]">{String(h).padStart(2, '0')}:00</span>
          </div>
        ))}
      </div>
      {/* Day columns */}
      {dates.map(date => {
        const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        const isToday = dateStr === todayStr;
        const dayTasks = tasksForDate(dateStr);
        const allDayTasks = dayTasks.filter(t => !hasSpecificTime(t));
        const timedTasks = dayTasks.filter(t => hasSpecificTime(t));

        return (
          <div key={dateStr} className={`flex-1 ${isDay ? '' : 'min-w-[140px]'} border-r border-[#2C3338] ${isToday ? 'bg-[#00E01A]/[0.02]' : ''}`}>
            {/* Day header */}
            <div className={`h-10 border-b border-[#2C3338] flex items-center justify-center gap-1 ${isToday ? 'bg-[#00E01A]/10' : ''}`}>
              <span className="text-[10px] text-[#555] uppercase">{DAYS_FR[(date.getDay() + 6) % 7]}</span>
              <span className={`text-sm font-bold ${isToday ? 'bg-[#00E01A] text-black w-7 h-7 rounded-full flex items-center justify-center' : 'text-white'}`}>{date.getDate()}</span>
              {date.getDate() <= 2 && <span className="text-[10px] text-[#555]">{MONTHS_FR[date.getMonth()].slice(0, 3)}</span>}
            </div>

            {/* All-day tasks section */}
            <div className={`border-b border-[#2C3338] p-1 ${allDayTasks.length > 0 ? 'min-h-[40px]' : 'h-10'} hover:bg-[#232829] cursor-pointer transition-colors`} onClick={() => onDayClick(dateStr)}>
              {allDayTasks.map(t => {
                const st = getStatus(t.status);
                const pri = getPriority(t.priority);
                const cat = getCategory(t.category);
                const CatIcon = cat.icon;
                return (
                  <button key={t.id} onClick={(e) => { e.stopPropagation(); onTaskClick(t); }} className={`w-full text-left px-2 py-1 rounded border mb-0.5 ${st.bg} ${st.border} hover:opacity-80 transition-opacity`} data-testid={`time-task-${t.id}`}>
                    <div className="flex items-center gap-1">
                      <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: pri.color }}></div>
                      <span className={`${isDay ? 'text-xs' : 'text-[11px]'} font-medium ${st.text} truncate flex-1`}>{t.title}</span>
                      <CatIcon size={10} className="text-[#555] flex-shrink-0" />
                    </div>
                    {isDay && t.description && <p className="text-[10px] text-[#888] mt-0.5 line-clamp-2">{t.description}</p>}
                    {isDay && (
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-[9px] px-1.5 py-0.5 rounded ${st.bg} ${st.text}`}>{st.label}</span>
                        <span className="text-[9px] text-[#555]">{cat.label}</span>
                        <span className="text-[9px] text-[#555]">{pri.label}</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Hour slots */}
            {HOURS.map(h => {
              const slotTasks = timedTasks.filter(t => getTaskHour(t) === h);
              return (
                <div key={h} className="h-14 border-b border-[#2C3338] p-0.5 hover:bg-[#232829] cursor-pointer transition-colors" onClick={() => onDayClick(dateStr)}>
                  {slotTasks.map(t => {
                    const st = getStatus(t.status);
                    const pri = getPriority(t.priority);
                    const cat = getCategory(t.category);
                    const CatIcon = cat.icon;
                    return (
                      <button key={t.id} onClick={(e) => { e.stopPropagation(); onTaskClick(t); }} className={`w-full text-left px-2 py-1 rounded border mb-0.5 ${st.bg} ${st.border} hover:opacity-80 transition-opacity`}>
                        <div className="flex items-center gap-1">
                          <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: pri.color }}></div>
                          <span className={`text-[11px] font-medium ${st.text} truncate flex-1`}>{t.title}</span>
                          <CatIcon size={10} className="text-[#555] flex-shrink-0" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// ==================== 15-DAY GRID ====================
function FortNightGrid({ dates, tasksForDate, todayStr, onTaskClick, onDayClick }) {
  const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return (
    <div className="grid grid-cols-5 sm:grid-cols-5">
      {dates.map(date => {
        const dateStr = fmtDate(date);
        const isToday = dateStr === todayStr;
        const dayTasks = tasksForDate(dateStr);
        const isWeekend = date.getDay() === 0 || date.getDay() === 6;

        return (
          <div key={dateStr} className={`min-h-[120px] border-r border-b border-[#2C3338] p-2 cursor-pointer hover:bg-[#232829] transition-colors ${isToday ? 'ring-1 ring-inset ring-[#00E01A]/40' : ''} ${isWeekend ? 'bg-[#191C1E]' : 'bg-[#1D2327]'}`} onClick={() => onDayClick(dateStr)}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1">
                <span className={`text-[10px] uppercase ${isToday ? 'text-[#00E01A]' : 'text-[#555]'}`}>{DAYS_FR[(date.getDay() + 6) % 7]}</span>
                <span className={`text-xs font-bold ${isToday ? 'bg-[#00E01A] text-black w-5 h-5 rounded-full flex items-center justify-center' : 'text-white'}`}>{date.getDate()}</span>
                {date.getDate() === 1 && <span className="text-[10px] text-[#555]">{MONTHS_FR[date.getMonth()].slice(0, 3)}</span>}
              </div>
              {dayTasks.length > 0 && <span className="text-[10px] bg-[#2C3338] text-[#8C8F94] px-1.5 py-0.5 rounded-full">{dayTasks.length}</span>}
            </div>
            <div className="space-y-0.5">
              {dayTasks.slice(0, 4).map(t => {
                const st = getStatus(t.status);
                return (
                  <button key={t.id} onClick={(e) => { e.stopPropagation(); onTaskClick(t); }} className={`w-full text-left px-1.5 py-0.5 rounded text-[10px] truncate border ${st.bg} ${st.border} ${st.text} hover:opacity-80`}>
                    {t.title}
                  </button>
                );
              })}
              {dayTasks.length > 4 && <span className="text-[10px] text-[#555]">+{dayTasks.length - 4}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ==================== KANBAN VIEW ====================
function KanbanView({ tasks, onTaskClick, onStatusChange, onEdit, draggedTask, setDraggedTask }) {
  const handleDragOver = (e) => e.preventDefault();

  const handleDrop = (e, newStatus) => {
    e.preventDefault();
    if (draggedTask && draggedTask.status !== newStatus) {
      onStatusChange(draggedTask.id, newStatus);
    }
    setDraggedTask(null);
  };

  return (
    <div className="grid grid-cols-4 gap-4" data-testid="kanban-view">
      {STATUSES.map(status => {
        const columnTasks = tasks.filter(t => t.status === status.id);
        return (
          <div key={status.id} className="bg-[#1D2327] rounded-xl border border-[#2C3338] overflow-hidden" onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, status.id)} data-testid={`kanban-col-${status.id}`}>
            <div className="p-3 border-b border-[#2C3338] flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: status.color }}></div>
              <h3 className="text-sm font-semibold text-white flex-1">{status.label}</h3>
              <span className="text-xs text-[#8C8F94] bg-[#2C3338] px-2 py-0.5 rounded-full">{columnTasks.length}</span>
            </div>
            <div className="p-2 space-y-2 max-h-[calc(100vh-320px)] overflow-y-auto">
              {columnTasks.map(task => (
                <KanbanCard key={task.id} task={task} onTaskClick={onTaskClick} onEdit={onEdit} setDraggedTask={setDraggedTask} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function KanbanCard({ task, onTaskClick, onEdit, setDraggedTask }) {
  const cat = getCategory(task.category);
  const pri = getPriority(task.priority);
  const CatIcon = cat.icon;
  const [isDragging, setIsDragging] = useState(false);

  const handleClick = () => {
    if (!isDragging) onEdit(task);
  };

  return (
    <div draggable onDragStart={() => { setIsDragging(true); setDraggedTask(task); }} onDragEnd={() => { setTimeout(() => setIsDragging(false), 50); setDraggedTask(null); }} onClick={handleClick} className="bg-[#141617] rounded-lg border border-[#2C3338] p-3 cursor-pointer active:cursor-grabbing hover:border-[#00E01A]/30 transition-all group" data-testid={`kanban-task-${task.id}`}>
      <div className="flex items-start justify-between mb-2">
        <h4 className="text-sm font-medium text-white flex-1 pr-2">{task.title}</h4>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button onClick={(e) => e.stopPropagation()} className="p-1 text-[#8C8F94] hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"><MoreVertical size={14} /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[#1D2327] border-[#2C3338]">
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onTaskClick(task); }} className="text-white hover:bg-[#2C3338]"><Eye size={14} className="mr-2" />Details</DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(task); }} className="text-white hover:bg-[#2C3338]"><Edit size={14} className="mr-2" />Modifier</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {task.description && <p className="text-xs text-[#8C8F94] mb-2 line-clamp-2">{task.description}</p>}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="flex items-center gap-1 text-[10px] text-[#8C8F94] bg-[#1D2327] px-1.5 py-0.5 rounded"><CatIcon size={10} />{cat.label}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {task.deadline && <span className="flex items-center gap-1 text-[10px] text-[#8C8F94]"><Clock size={10} />{new Date(task.deadline).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>}
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: pri.color }} title={pri.label}></div>
        </div>
      </div>
      {task.comments?.length > 0 && <div className="flex items-center gap-1 mt-2 text-[10px] text-[#8C8F94]"><MessageSquare size={10} />{task.comments.length}</div>}
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        {task.file_attachments?.length > 0 && (
          <span className="flex items-center gap-1 text-[10px] text-amber-400"><Paperclip size={10} />{task.file_attachments.length}</span>
        )}
        {task.linked_prospect_name && (
          <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-purple-500/10 border border-purple-500/30 text-purple-400">
            <Link2 size={9} />{task.linked_prospect_name}
          </span>
        )}
      </div>
    </div>
  );
}

// ==================== DAY DETAIL MODAL ====================
function DayDetailModal({ dateStr, tasks, onClose, onTaskClick, onEdit, onNewTask, onStatusChange }) {
  const dayTasks = useMemo(() => {
    return tasks.filter(t => {
      const d = t.deadline || t.start_date;
      if (!d) return false;
      const td = new Date(d);
      const tdStr = `${td.getFullYear()}-${String(td.getMonth() + 1).padStart(2, '0')}-${String(td.getDate()).padStart(2, '0')}`;
      return tdStr === dateStr;
    });
  }, [tasks, dateStr]);

  const dateObj = new Date(dateStr + 'T12:00:00');
  const dateLabel = dateObj.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#1D2327] border-[#2C3338] text-white max-w-lg max-h-[80vh] overflow-y-auto" data-testid="day-detail-modal">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-lg capitalize">{dateLabel}</DialogTitle>
            <span className="text-xs bg-[#2C3338] text-[#8C8F94] px-2 py-1 rounded-full">{dayTasks.length} tache{dayTasks.length !== 1 ? 's' : ''}</span>
          </div>
        </DialogHeader>

        {dayTasks.length === 0 ? (
          <div className="text-center py-8">
            <CalendarIcon size={32} className="mx-auto text-[#2C3338] mb-2" />
            <p className="text-[#8C8F94] text-sm">Aucune tache pour ce jour</p>
          </div>
        ) : (
          <div className="space-y-2 mt-2">
            {dayTasks.map(t => {
              const st = getStatus(t.status);
              const pri = getPriority(t.priority);
              const cat = getCategory(t.category);
              const CatIcon = cat.icon;
              return (
                <div key={t.id} className={`p-3 rounded-lg border ${st.bg} ${st.border} hover:opacity-90 transition-all cursor-pointer`} onClick={() => onTaskClick(t)} data-testid={`day-task-${t.id}`}>
                  <div className="flex items-start justify-between mb-1.5">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: pri.color }}></div>
                      <h4 className={`text-sm font-medium ${st.text} truncate`}>{t.title}</h4>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={(e) => { e.stopPropagation(); onEdit(t); }} className="p-1 text-[#8C8F94] hover:text-white rounded transition-colors" data-testid={`day-edit-${t.id}`}><Edit size={13} /></button>
                    </div>
                  </div>
                  {t.description && <p className="text-xs text-[#8C8F94] mb-1.5 line-clamp-2 ml-4">{t.description}</p>}
                  <div className="flex items-center gap-2 ml-4">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${st.bg} ${st.text}`}>{st.label}</span>
                    <span className="flex items-center gap-1 text-[10px] text-[#8C8F94]"><CatIcon size={10} />{cat.label}</span>
                    {t.file_attachments?.length > 0 && (
                      <span className="flex items-center gap-1 text-[10px] text-amber-400"><Paperclip size={9} />{t.file_attachments.length}</span>
                    )}
                    {t.linked_prospect_name && (
                      <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-purple-500/10 border border-purple-500/30 text-purple-400">
                        <Link2 size={9} />{t.linked_prospect_name}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <button onClick={onNewTask} className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#00E01A] text-black font-medium rounded-lg text-sm hover:bg-[#00C016] transition-colors" data-testid="day-new-task-btn">
          <Plus size={16} />Nouvelle tache
        </button>
      </DialogContent>
    </Dialog>
  );
}

// ==================== TASK FORM MODAL ====================
function TaskFormModal({ task, onSave, onClose, prospects = [] }) {
  const [form, setForm] = useState({
    title: task?.title || '',
    description: task?.description || '',
    category: task?.category || 'autre',
    status: task?.status || 'todo',
    priority: task?.priority || 'medium',
    deadline: task?.deadline ? task.deadline.split('T')[0] : '',
    start_date: task?.start_date ? task.start_date.split('T')[0] : '',
    tags: task?.tags || [],
    linked_prospect_id: task?.linked_prospect_id || '',
    linked_prospect_type: task?.linked_prospect_type || '',
    linked_prospect_name: task?.linked_prospect_name || '',
  });
  const [prospectSearch, setProspectSearch] = useState('');
  const [showProspectDropdown, setShowProspectDropdown] = useState(false);

  const filteredProspects = useMemo(() => {
    if (!prospectSearch.trim()) return prospects.slice(0, 20);
    const q = prospectSearch.toLowerCase();
    return prospects.filter(p => (p.org || '').toLowerCase().includes(q) || (p.nom || '').toLowerCase().includes(q) || (p.email || '').toLowerCase().includes(q)).slice(0, 20);
  }, [prospects, prospectSearch]);

  const selectProspect = (p) => {
    setForm(f => ({
      ...f,
      linked_prospect_id: p.id,
      linked_prospect_type: p.type || 'cse',
      linked_prospect_name: p.org || p.nom || p.email || '',
    }));
    setProspectSearch('');
    setShowProspectDropdown(false);
  };

  const clearProspect = () => {
    setForm(f => ({ ...f, linked_prospect_id: '', linked_prospect_type: '', linked_prospect_name: '' }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error('Titre requis');
    onSave({
      ...form,
      deadline: form.deadline ? new Date(form.deadline).toISOString() : null,
      start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
      linked_prospect_id: form.linked_prospect_id || null,
      linked_prospect_type: form.linked_prospect_type || null,
      linked_prospect_name: form.linked_prospect_name || null,
    });
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#1D2327] border-[#2C3338] text-white max-w-lg" data-testid="task-form-modal">
        <DialogHeader><DialogTitle>{task?.id ? 'Modifier la tache' : 'Nouvelle tache'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs text-[#8C8F94] mb-1">Titre *</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="w-full px-3 py-2 bg-[#141617] border border-[#2C3338] rounded-lg text-white text-sm focus:border-[#00E01A] focus:outline-none" placeholder="Ex: Creer le post Instagram lancement Awake" data-testid="task-title-input" />
          </div>
          <div>
            <label className="block text-xs text-[#8C8F94] mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} className="w-full px-3 py-2 bg-[#141617] border border-[#2C3338] rounded-lg text-white text-sm focus:border-[#00E01A] focus:outline-none resize-none" placeholder="Details de la tache..." data-testid="task-desc-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[#8C8F94] mb-1">Categorie</label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full px-3 py-2 bg-[#141617] border border-[#2C3338] rounded-lg text-white text-sm focus:border-[#00E01A] focus:outline-none" data-testid="task-category-select">
                {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-[#8C8F94] mb-1">Priorite</label>
              <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} className="w-full px-3 py-2 bg-[#141617] border border-[#2C3338] rounded-lg text-white text-sm focus:border-[#00E01A] focus:outline-none" data-testid="task-priority-select">
                {PRIORITIES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[#8C8F94] mb-1">Date debut</label>
              <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} className="w-full px-3 py-2 bg-[#141617] border border-[#2C3338] rounded-lg text-white text-sm focus:border-[#00E01A] focus:outline-none" data-testid="task-start-input" />
            </div>
            <div>
              <label className="block text-xs text-[#8C8F94] mb-1">Deadline</label>
              <input type="date" value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} className="w-full px-3 py-2 bg-[#141617] border border-[#2C3338] rounded-lg text-white text-sm focus:border-[#00E01A] focus:outline-none" data-testid="task-deadline-input" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-[#8C8F94] mb-1">Lier a un prospect</label>
            {form.linked_prospect_id ? (
              <div className="flex items-center gap-2 px-3 py-2 bg-[#141617] border border-purple-500/30 rounded-lg">
                <Link2 size={14} className="text-purple-400" />
                <span className={`text-xs px-1.5 py-0.5 rounded ${form.linked_prospect_type === 'client' ? 'bg-blue-500/10 text-blue-400' : 'bg-amber-500/10 text-amber-400'}`}>
                  {form.linked_prospect_type === 'client' ? 'Client' : 'CSE'}
                </span>
                <span className="text-sm text-white flex-1 truncate">{form.linked_prospect_name}</span>
                <button type="button" onClick={clearProspect} className="p-0.5 text-[#8C8F94] hover:text-red-400"><X size={14} /></button>
              </div>
            ) : (
              <div className="relative">
                <div className="flex items-center bg-[#141617] border border-[#2C3338] rounded-lg focus-within:border-[#00E01A]">
                  <Search size={14} className="ml-3 text-[#555]" />
                  <input
                    value={prospectSearch}
                    onChange={e => { setProspectSearch(e.target.value); setShowProspectDropdown(true); }}
                    onFocus={() => setShowProspectDropdown(true)}
                    className="w-full px-2 py-2 bg-transparent text-white text-sm focus:outline-none"
                    placeholder="Rechercher un prospect CSE ou Client..."
                    data-testid="prospect-search-input"
                  />
                </div>
                {showProspectDropdown && (
                  <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto bg-[#141617] border border-[#2C3338] rounded-lg shadow-xl" data-testid="prospect-dropdown">
                    {filteredProspects.length === 0 ? (
                      <div className="p-3 text-xs text-[#8C8F94] text-center">Aucun prospect trouve</div>
                    ) : (
                      filteredProspects.map(p => (
                        <button key={p.id} type="button" onClick={() => selectProspect(p)} className="w-full text-left px-3 py-2 hover:bg-[#2C3338] transition-colors flex items-center gap-2" data-testid={`prospect-option-${p.id}`}>
                          {p.type === 'client' ? <ShoppingCart size={12} className="text-blue-400 flex-shrink-0" /> : <Building2 size={12} className="text-amber-400 flex-shrink-0" />}
                          <span className="text-sm text-white truncate flex-1">{p.org || p.nom}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${p.type === 'client' ? 'bg-blue-500/10 text-blue-400' : 'bg-amber-500/10 text-amber-400'}`}>
                            {p.type === 'client' ? 'Client' : 'CSE'}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs text-[#8C8F94] mb-1">Statut</label>
            <div className="flex gap-2">
              {STATUSES.map(s => (
                <button key={s.id} type="button" onClick={() => setForm(f => ({ ...f, status: s.id }))} className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all border ${form.status === s.id ? `${s.bg} ${s.border} ${s.text}` : 'bg-[#141617] border-[#2C3338] text-[#8C8F94] hover:border-[#555]'}`} data-testid={`task-status-${s.id}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-[#8C8F94] hover:text-white text-sm">Annuler</button>
            <button type="submit" className="px-6 py-2 bg-[#00E01A] text-black font-medium rounded-lg text-sm hover:bg-[#00C016]" data-testid="task-save-btn">{task?.id ? 'Mettre a jour' : 'Creer'}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ==================== TASK DETAIL MODAL (ENHANCED) ====================
function TaskDetailModal({ task, onClose, onEdit, onDelete, onComment, onStatusChange, onReload }) {
  const { token } = useAuth();
  const [comment, setComment] = useState('');
  const [uploading, setUploading] = useState(false);
  const [activeSection, setActiveSection] = useState('details');
  const fileInputRef = useRef(null);
  const status = getStatus(task.status);
  const cat = getCategory(task.category);
  const pri = getPriority(task.priority);
  const CatIcon = cat.icon;
  const isDone = task.status === 'done';
  const attachments = task.file_attachments || [];
  const activityLog = task.activity_log || [];

  const handleSubmitComment = (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    onComment(task.id, comment.trim());
    setComment('');
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        const r = await fetch(`${API_URL}/api/marketing/tasks/${task.id}/attachments`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
        });
        if (!r.ok) throw new Error(await r.text());
      }
      toast.success(`${files.length} fichier(s) ajoute(s)`);
      if (onReload) onReload();
    } catch { toast.error('Erreur upload fichier'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Supprimer cette piece jointe ?')) return;
    try {
      await fetch(`${API_URL}/api/marketing/tasks/${task.id}/attachments/${attachmentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      toast.success('Piece jointe supprimee');
      if (onReload) onReload();
    } catch { toast.error('Erreur suppression'); }
  };

  const isImage = (ct) => ct?.startsWith('image/');
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  };

  const LOG_ICONS = {
    creation: { icon: Plus, color: 'text-green-400' },
    statut: { icon: ArrowRight, color: 'text-blue-400' },
    fichier: { icon: Paperclip, color: 'text-amber-400' },
    commentaire: { icon: MessageSquare, color: 'text-purple-400' },
    liaison: { icon: Link2, color: 'text-pink-400' },
    modification: { icon: Edit, color: 'text-cyan-400' },
  };

  const sections = [
    { id: 'details', label: 'Details' },
    { id: 'fichiers', label: `Fichiers (${attachments.length})` },
    { id: 'activite', label: 'Activite' },
  ];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#1D2327] border-[#2C3338] text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="task-detail-modal">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${status.bg} ${status.border} ${status.text}`}>{status.label}</span>
                <span className="flex items-center gap-1 text-[10px] text-[#8C8F94]"><CatIcon size={10} />{cat.label}</span>
                <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full" style={{ backgroundColor: pri.color }}></div><span className="text-[10px] text-[#8C8F94]">{pri.label}</span></div>
                {attachments.length > 0 && <span className="flex items-center gap-1 text-[10px] text-amber-400"><Paperclip size={10} />{attachments.length}</span>}
              </div>
              <DialogTitle className="text-lg">{task.title}</DialogTitle>
            </div>
          </div>
        </DialogHeader>

        {task.description && <p className="text-sm text-[#8C8F94] mt-2">{task.description}</p>}

        {task.linked_prospect_name && (
          <div className="mt-3 flex items-center gap-2 p-2.5 bg-purple-500/5 border border-purple-500/20 rounded-lg">
            <Link2 size={14} className="text-purple-400" />
            <span className="text-xs text-[#8C8F94]">Lie a :</span>
            <span className={`text-xs px-1.5 py-0.5 rounded ${task.linked_prospect_type === 'client' ? 'bg-blue-500/10 text-blue-400' : 'bg-amber-500/10 text-amber-400'}`}>
              {task.linked_prospect_type === 'client' ? 'Client' : 'CSE'}
            </span>
            <span className="text-sm text-white font-medium">{task.linked_prospect_name}</span>
          </div>
        )}

        {/* Done summary banner */}
        {isDone && (
          <div className="mt-3 p-3 bg-green-500/5 border border-green-500/20 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 size={16} className="text-green-400" />
              <span className="text-sm font-medium text-green-400">Tache terminee</span>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div><p className="text-lg font-bold text-white">{attachments.length}</p><p className="text-[10px] text-[#8C8F94]">Fichiers</p></div>
              <div><p className="text-lg font-bold text-white">{(task.comments || []).length}</p><p className="text-[10px] text-[#8C8F94]">Commentaires</p></div>
              <div><p className="text-lg font-bold text-white">{activityLog.length}</p><p className="text-[10px] text-[#8C8F94]">Actions</p></div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mt-4 p-3 bg-[#141617] rounded-lg">
          {task.start_date && <div><p className="text-[10px] text-[#555]">Debut</p><p className="text-sm text-white">{new Date(task.start_date).toLocaleDateString('fr-FR')}</p></div>}
          {task.deadline && <div><p className="text-[10px] text-[#555]">Deadline</p><p className="text-sm text-white">{new Date(task.deadline).toLocaleDateString('fr-FR')}</p></div>}
          <div><p className="text-[10px] text-[#555]">Cree le</p><p className="text-sm text-white">{new Date(task.created_at).toLocaleDateString('fr-FR')}</p></div>
          <div><p className="text-[10px] text-[#555]">Modifie le</p><p className="text-sm text-white">{new Date(task.updated_at).toLocaleDateString('fr-FR')}</p></div>
        </div>

        {/* Status change */}
        <div className="mt-4">
          <p className="text-xs text-[#8C8F94] mb-2">Changer le statut</p>
          <div className="flex gap-2">
            {STATUSES.map(s => (
              <button key={s.id} onClick={() => onStatusChange(task.id, s.id)} className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-all border ${task.status === s.id ? `${s.bg} ${s.border} ${s.text}` : 'bg-[#141617] border-[#2C3338] text-[#8C8F94] hover:border-[#555]'}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Section tabs */}
        <div className="flex gap-1 mt-4 bg-[#141617] rounded-lg p-0.5">
          {sections.map(s => (
            <button key={s.id} onClick={() => setActiveSection(s.id)} className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-all ${activeSection === s.id ? 'bg-[#2C3338] text-white' : 'text-[#8C8F94] hover:text-white'}`} data-testid={`section-${s.id}`}>
              {s.label}
            </button>
          ))}
        </div>

        {/* Details section - comments */}
        {activeSection === 'details' && (
          <div className="mt-3">
            <p className="text-xs text-[#8C8F94] mb-2">Commentaires ({task.comments?.length || 0})</p>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {(task.comments || []).map(c => (
                <div key={c.id} className="p-2 bg-[#141617] rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] text-[#00E01A] font-medium">{c.author_name || c.author}</span>
                    <span className="text-[10px] text-[#555]">{new Date(c.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                  <p className="text-xs text-white">{c.text}</p>
                </div>
              ))}
            </div>
            <form onSubmit={handleSubmitComment} className="flex gap-2 mt-2">
              <input value={comment} onChange={e => setComment(e.target.value)} className="flex-1 px-3 py-2 bg-[#141617] border border-[#2C3338] rounded-lg text-white text-sm focus:border-[#00E01A] focus:outline-none" placeholder="Ajouter un commentaire..." data-testid="comment-input" />
              <button type="submit" className="p-2 bg-[#00E01A] text-black rounded-lg hover:bg-[#00C016]" data-testid="comment-submit-btn"><Send size={16} /></button>
            </form>
          </div>
        )}

        {/* Files section */}
        {activeSection === 'fichiers' && (
          <div className="mt-3 space-y-3">
            {/* Upload zone */}
            <div className="relative">
              <input ref={fileInputRef} type="file" multiple onChange={handleFileUpload} className="hidden" data-testid="file-upload-input" />
              <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                className="w-full border-2 border-dashed border-[#2C3338] rounded-lg p-4 flex flex-col items-center gap-2 text-[#8C8F94] hover:border-[#00E01A] hover:text-[#00E01A] transition-colors disabled:opacity-50"
                data-testid="file-upload-btn">
                {uploading ? (
                  <div className="w-5 h-5 border-2 border-[#00E01A] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Upload size={20} />
                )}
                <span className="text-xs">{uploading ? 'Upload en cours...' : 'Cliquer pour ajouter des fichiers'}</span>
                <span className="text-[10px] text-[#555]">Photos, PDF, documents, emails...</span>
              </button>
            </div>

            {/* Attachments grid */}
            {attachments.length === 0 ? (
              <div className="text-center py-4">
                <Paperclip size={24} className="mx-auto text-[#2C3338] mb-1" />
                <p className="text-[#8C8F94] text-xs">Aucune piece jointe</p>
              </div>
            ) : (
              <div className="space-y-2">
                {attachments.map(a => (
                  <div key={a.id} className="flex items-center gap-3 p-2.5 bg-[#141617] rounded-lg border border-[#2C3338] group" data-testid={`attachment-${a.id}`}>
                    {isImage(a.content_type) ? (
                      <div className="w-10 h-10 rounded bg-[#2C3338] flex items-center justify-center overflow-hidden flex-shrink-0">
                        <img src={`${API_URL}${a.url}`} alt={a.filename} className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded bg-[#2C3338] flex items-center justify-center flex-shrink-0">
                        <FileText size={18} className="text-[#8C8F94]" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{a.filename}</p>
                      <p className="text-[10px] text-[#8C8F94]">{formatSize(a.size)} — {a.uploaded_by_name || a.uploaded_by} — {new Date(a.uploaded_at).toLocaleDateString('fr-FR')}</p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <a href={`${API_URL}${a.url}`} target="_blank" rel="noreferrer" className="p-1.5 text-[#8C8F94] hover:text-[#00E01A] rounded transition-colors" data-testid={`download-${a.id}`}><Download size={14} /></a>
                      <button onClick={() => handleDeleteAttachment(a.id)} className="p-1.5 text-[#8C8F94] hover:text-red-400 rounded transition-colors" data-testid={`delete-attachment-${a.id}`}><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Activity section */}
        {activeSection === 'activite' && (
          <div className="mt-3">
            {activityLog.length === 0 ? (
              <div className="text-center py-4">
                <Activity size={24} className="mx-auto text-[#2C3338] mb-1" />
                <p className="text-[#8C8F94] text-xs">Aucune activite enregistree</p>
              </div>
            ) : (
              <div className="space-y-0 relative">
                <div className="absolute left-[15px] top-2 bottom-2 w-px bg-[#2C3338]"></div>
                {[...activityLog].reverse().map(log => {
                  const logConfig = LOG_ICONS[log.action] || LOG_ICONS.modification;
                  const LogIcon = logConfig.icon;
                  return (
                    <div key={log.id} className="flex items-start gap-3 py-2 relative" data-testid={`log-${log.id}`}>
                      <div className={`w-[30px] h-[30px] rounded-full bg-[#141617] border border-[#2C3338] flex items-center justify-center flex-shrink-0 z-10 ${logConfig.color}`}>
                        <LogIcon size={13} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-white">{log.detail}</p>
                        <p className="text-[10px] text-[#555]">{log.user_name || log.user_email} — {new Date(log.timestamp).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-between mt-4 pt-3 border-t border-[#2C3338]">
          <button onClick={() => onDelete(task.id)} className="flex items-center gap-2 px-3 py-1.5 text-red-400 hover:bg-red-500/10 rounded-lg text-sm" data-testid="task-delete-btn"><Trash2 size={14} />Supprimer</button>
          <button onClick={() => onEdit(task)} className="flex items-center gap-2 px-4 py-1.5 bg-[#00E01A] text-black font-medium rounded-lg text-sm hover:bg-[#00C016]" data-testid="task-edit-btn"><Edit size={14} />Modifier</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
